import { createTheme } from "@mui/material/styles";
import type { Shadows } from "@mui/material/styles";

const shadows = Array(25).fill("none") as Shadows;

shadows[1] = "0px 1px 2px rgba(15, 23, 42, 0.04)";

shadows[2] = "0px 4px 12px rgba(15, 23, 42, 0.08)";

shadows[3] = "0px 10px 24px rgba(15, 23, 42, 0.12)";

shadows[4] = "0px 16px 40px rgba(15, 23, 42, 0.16)";

export const theme = createTheme({
  layout: {
    contentMaxWidth: 1200,
  },

  spacing: 4,

  breakpoints: {
    values: {
      xs: 0,
      sm: 430,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },

  shape: {
    borderRadius: 8,
  },

  shadows,

  palette: {
    primary: {
      extralight: "#EEE7FE",
      hover: "#E3DDFE",
      light: "#9B87FC",
      main: "#684CE5",
      dark: "#25257F",
      contrastText: "#FFFFFF",
    },

    text: {
      primary: "#0F172A",
      secondary: "#5D5555",
      disabled: "#757575",
      contrast: "#FFFFFF",
      link: "#1b72e8",
    },

    background: {
      default: "#F8F8F8",
      paper: "#FFFFFF",
      accent: "#EEE7FE",
      disabled: "#F3F3F3",
      inverse: "#25257F",
      proChip:
        "#FFFFFF linear-gradient(90deg, #41BBF033, #41BBF099, #7355FB99, #7355FB33)",
      proChipText:
        "#FFFFFF linear-gradient(90deg, #41BBF0B2, #41BBF0FF, #7355FBFF, #7355FBB2)",
      newChip: "linear-gradient(180deg, #7355FB 30.77%, #25257F 100%)",
    },

    icon: {
      primary: "#464646",
      secondary: "#757575",
      disabled: "#BDBDBD",
      contrast: "#FFFFFF",
    },

    border: {
      default: "#EAEAEA",
      strong: "#D6D6D6",
    },

    divider: "#EAEAEA",

    success: {
      light: "#E8F5E9",
      main: "#4CAF50",
      dark: "#2E7D32",
      contrastText: "#2E7D32",
    },

    warning: {
      light: "#FFF4E5",
      main: "#F59E0B",
      dark: "#B45309",
      contrastText: "#B45309",
    },

    error: {
      light: "#FEECEC",
      main: "#DC2626",
      dark: "#991B1B",
      contrastText: "#991B1B",
    },

    info: {
      light: "rgba(3, 169, 244, 0.1)",
      hover: "rgba(3, 169, 244, 0.18)",
      main: "#03A9F4",
      dark: "#0D3C61",
      contrastText: "#0D3C61",
    },

    action: {
      hover: "rgba(104, 76, 229, 0.08)",
      selected: "rgba(104, 76, 229, 0.12)",
      focus: "rgba(104, 76, 229, 0.20)",
      active: "#684CE5",
      disabled: "rgba(15, 23, 42, 0.38)",
      disabledBackground: "#F3F3F3",
    },

    documentTypes: {
      PRESCRIPTION: "#41BBF0",
      COMPOUNDED_PRESCRIPTION: "#7BECFE",
      MEDICAL_CERTIFICATE: "#7355FB",
      MEDICAL_ORDER: "#25257F",
    },
  },

  typography: {
    fontFamily: 'Poppins, Inter, system-ui, -apple-system, "Segoe UI", sans-serif',

    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,

    h1: {
      fontSize: "60px",
      lineHeight: "81px",
      fontWeight: 700,
    },
    h2: {
      fontSize: "40px",
      lineHeight: "48px",
      fontWeight: 700,
    },
    h3: {
      fontSize: "32px",
      lineHeight: "40px",
      fontWeight: 700,
    },
    h4: {
      fontSize: "24px",
      lineHeight: "32px",
      fontWeight: 600,
    },
    h5: {
      fontSize: "20px",
      lineHeight: "28px",
      fontWeight: 600,
    },
    h6: {
      fontSize: "18px",
      lineHeight: "26px",
      fontWeight: 600,
    },

    subtitle1: {
      fontSize: "16px",
      lineHeight: "23px",
      fontWeight: 600,
    },
    subtitle2: {
      fontSize: "14px",
      lineHeight: "19px",
      fontWeight: 500,
    },

    body1: {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 400,
    },
    body2: {
      fontSize: "14px",
      lineHeight: "21px",
      fontWeight: 400,
    },

    button: {
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 600,
      textTransform: "none",
    },

    caption: {
      fontSize: "12px",
      lineHeight: "17px",
      fontWeight: 500,
    },

    overline: {
      fontSize: "8px",
      lineHeight: "normal",
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: 0,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#F8F8F8",
          color: "#0F172A",
        },
      },
    },

    MuiContainer: {
      styleOverrides: {
        root: {
          width: "100%",
          maxWidth: "1200px !important",
          marginLeft: "auto",
          marginRight: "auto",
          paddingLeft: 16,
          paddingRight: 16,
          "@media (min-width:768px)": {
            paddingLeft: 24,
            paddingRight: 24,
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          color: "#0F172A",
          boxShadow: "none",
          borderBottom: "1px solid #EAEAEA",
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          borderRadius: 12,
          boxShadow: "none",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 48,
          paddingInline: 20,
          borderRadius: 8,
          boxShadow: "none",
          textTransform: "none",
        },
        containedPrimary: {
          borderRadius: 9999,
          color: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#25257F",
            boxShadow: "none",
          },
        },
        outlined: {
          borderColor: "#EAEAEA",
          "&:hover": {
            borderColor: "#D6D6D6",
            backgroundColor: "rgba(15, 23, 42, 0.02)",
          },
        },
        text: {
          "&:hover": {
            backgroundColor: "rgba(104, 76, 229, 0.06)",
          },
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: "#684CE5",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
          "&:focus-visible": {
            outline: "2px solid #684CE5",
            outlineOffset: 2,
            borderRadius: 4,
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "#0F172A",
          "&.Mui-focused": {
            color: "#0F172A",
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: "16px",
          lineHeight: "24px",
          fontWeight: 400,
          "::placeholder": {
            color: "#757575",
            opacity: 1,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 48,
          borderRadius: 8,
          backgroundColor: "#FFFFFF",
          boxShadow: "none",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#EAEAEA",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D6D6D6",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#684CE5",
            borderWidth: 1.5,
          },
          "&.Mui-disabled": {
            backgroundColor: "#F3F3F3",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#EAEAEA",
            },
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: "unset",
          display: "flex",
          alignItems: "center",
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#EAEAEA",
        },
      },
    },
  },
});
